import { InventoryAsset } from '../../../types';
import { ServiceRequest } from '../../../Api/ServiceRequest/serviceRequestApi';
import {
  formatLocation,
  isPointToPoint,
  requiresAsset,
  NEW_SERVICE_REQUEST,
  RequestSnapshot,
  CHANGE_REQUEST,
  DISCONNECT_REQUEST,
  REPLACE_REQUEST
} from './serviceRequestModel';

interface LocationSources {
  displayLocationCode: boolean;
  scopedInventory: InventoryAsset[];
  installedHere: InventoryAsset[];
}

export const buildServiceRequest = (
  snap: RequestSnapshot,
  { displayLocationCode, scopedInventory, installedHere }: LocationSources
): ServiceRequest => {
  const isP2P = isPointToPoint(snap.productSelection);
  const snapShowAssetSelection = requiresAsset(snap.request_type, snap.productSelection);

  const resolveDisplayedLocation = (locationValue: string): string => {
    if (!displayLocationCode) return locationValue;
    const known =
      scopedInventory.find((item) => item.addressInfo?.number === locationValue) ??
      installedHere.find((item) => item.addressInfo?.number === locationValue);
    return known ? formatLocation(known, false) : locationValue;
  };

  const locationsShown = !isP2P && ![CHANGE_REQUEST, DISCONNECT_REQUEST].includes(snap.request_type);
  const pickedLocations = locationsShown ? snap.locationSelections : [];

  const requestBody: ServiceRequest = {
    request_type: snap.request_type,
    service_type: snap.productSelection ? `${snap.productSelection.family} - ${snap.productSelection.name}` : '',
    locations: pickedLocations.map(resolveDisplayedLocation),
    new_locations: snap.newLocations,
    asset_id: snapShowAssetSelection ? snap.selectedAsset?.id : undefined,
    details: snap.details
  };

  if (snap.request_type === DISCONNECT_REQUEST) {
    requestBody.asset_id = snap.selectedDisconnectInventories[0]?.id;
    requestBody.locations = snap.selectedDisconnectInventories.map((i) => i.address);
    requestBody.new_locations = [];
    requestBody.preferred_term = undefined;
  }

  // Asset IDs let the backend use the assets' SF locationIds instead of looking them up by address.
  if ([NEW_SERVICE_REQUEST, REPLACE_REQUEST].includes(snap.request_type) && pickedLocations.length > 0) {
    const ofProduct = (item: InventoryAsset) => item.product?.id === snap.productSelection?.id;
    // One label can hold several services; a Replace names the one of the chosen product.
    const chosenProductFirst = [...installedHere, ...scopedInventory].sort(
      (x, y) => Number(ofProduct(y)) - Number(ofProduct(x))
    );
    const matchedAssets = pickedLocations
      .map((loc) => chosenProductFirst.find((item) => formatLocation(item, displayLocationCode) === loc))
      .filter((item): item is InventoryAsset => item !== undefined);
    if (matchedAssets.length > 0) {
      if (snap.request_type === REPLACE_REQUEST) {
        requestBody.asset_id = matchedAssets[0].id; // first asset drives Source_Id_c on quote/opportunity
      }
      requestBody.asset_ids = matchedAssets.map((item) => item.id);
    }
  }

  if (isP2P && snap.request_type !== CHANGE_REQUEST && snap.request_type !== DISCONNECT_REQUEST) {
    requestBody.locations = [snap.locationA, snap.locationZ]
      .filter((v): v is string => Boolean(v))
      .map(resolveDisplayedLocation);
    requestBody.new_locations = snap.newLocations;
  }

  if (Object.keys(snap.schemaFormData).length > 0) {
    requestBody.advanced_fields_type = snap.productSelection?.family;
    requestBody.advanced_fields = snap.schemaFormData;
  }

  if (snap.request_type === NEW_SERVICE_REQUEST) {
    requestBody.preferred_term = snap.preferred_term.length > 0 ? snap.preferred_term.join(', ') : undefined;
  }

  return requestBody;
};
